import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";

const HERO_IMG = "https://images.pexels.com/photos/32216281/pexels-photo-32216281.png";

export default function PublicApply() {
  const { publicId } = useParams();
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    mobile: "",
    current_location: "",
    total_experience: "",
    relevant_experience: "",
    current_company: "",
    current_ctc: "",
    expected_ctc: "",
    notice_period: "",
    highest_qualification: "",
    skills: "",
    portfolio_url: "",
    resume_text: "",
    resume_file_b64: "",
    resume_filename: "",
    consent: false,
  });

  useEffect(() => {
    api
      .get(`/public/requisitions/${publicId}`)
      .then((r) => setJob(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Job unavailable"));
  }, [publicId]);

  const change = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Resume must be under 5 MB");
      return;
    }
    const buf = await file.arrayBuffer();
    const bytes = new Uint8Array(buf);
    let bin = "";
    for (let i = 0; i < bytes.length; i += 0x8000) {
      bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
    }
    const b64 = btoa(bin);
    setForm({ ...form, resume_file_b64: b64, resume_filename: file.name });
    toast.success(`Attached ${file.name}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Please consent to proceed");
    if (!form.resume_file_b64 || !form.resume_filename) {
      return toast.error("Please upload your resume (PDF, max 5 MB)");
    }
    setBusy(true);
    try {
      await api.post(`/public/requisitions/${publicId}/apply`, {
        ...form,
        total_experience: Number(form.total_experience || 0),
        relevant_experience: Number(form.relevant_experience || 0),
        current_ctc: form.current_ctc === "" ? null : Number(form.current_ctc),
        expected_ctc: form.expected_ctc === "" ? null : Number(form.expected_ctc),
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadJd = async () => {
    try {
      const r = await api.get(`/public/requisitions/${publicId}/jd`);
      const { filename, mime, b64 } = r.data;
      const bin = atob(b64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const blob = new Blob([arr], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to download JD");
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-quiet">Status</div>
          <h1 className="font-serif text-4xl text-ink mt-2">Job not available.</h1>
          <p className="text-quiet mt-3 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!job) return <div className="p-8 text-quiet">Loading…</div>;

  if (submitted) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center p-8">
        <div className="max-w-md w-full bg-white border border-rule p-8 fade-up" data-testid="apply-success">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-quiet">Application received</div>
          <h1 className="font-serif text-4xl text-ink mt-2 leading-none">Thank you.</h1>
          <p className="text-quiet mt-4 text-sm">
            Your application for <span className="text-ink">{job.title}</span> has been received. Our team will review
            your profile and revert at the earliest. Good luck.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper" data-testid="public-apply-page">
      <div className="border-b border-rule bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-10 grid grid-cols-1 lg:grid-cols-5 gap-8 items-end">
          <div className="lg:col-span-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-quiet">
              {job.requisition_code} · {job.department} · {job.location}
            </div>
            <h1 className="font-serif text-5xl lg:text-6xl text-ink mt-3 leading-[0.95]">{job.title}</h1>
            <div className="mt-4 flex flex-wrap gap-2">
              <Tag>{job.employment_type.replace("_", " ")}</Tag>
              <Tag>
                {job.experience_min}–{job.experience_max} yrs
              </Tag>
              {job.ctc_min && <Tag>{`${job.ctc_min}–${job.ctc_max} LPA`}</Tag>}
            </div>
          </div>
          <div className="lg:col-span-2">
            <div className="aspect-[4/3] border border-rule overflow-hidden">
              <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-10 grid grid-cols-1 lg:grid-cols-5 gap-8">
        <aside className="lg:col-span-2 lg:sticky lg:top-8 self-start">
          <div className="bg-white border border-rule p-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">About the role</div>
            <p className="mt-2 text-sm text-ink whitespace-pre-wrap leading-relaxed max-h-[400px] overflow-auto">
              {job.description}
            </p>
            <div className="mt-4 pt-4 border-t border-rule">
              <div className="font-mono text-[10px] uppercase tracking-wider text-quiet">Skills</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {(job.skills || "").split(",").map((s) => s.trim()).filter(Boolean).map((s) => (
                  <span key={s} className="px-2 py-1 bg-paper border border-rule text-xs font-mono">{s}</span>
                ))}
              </div>
            </div>
            {job.jd_filename && (
              <div className="mt-4 pt-4 border-t border-rule">
                <button
                  data-testid="public-download-jd"
                  type="button"
                  onClick={downloadJd}
                  className="w-full bg-ink hover:bg-black text-white px-3 py-2 text-xs font-mono uppercase tracking-wider"
                >
                  Download JD · {job.jd_filename} ↓
                </button>
              </div>
            )}
          </div>
        </aside>

        <form onSubmit={submit} className="lg:col-span-3 bg-white border border-rule p-6 space-y-5">
          <h2 className="font-serif text-3xl text-ink">Apply.</h2>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Full name">
              <input data-testid="apply-name" required value={form.full_name} onChange={change("full_name")} className={inp} />
            </Field>
            <Field label="Email">
              <input data-testid="apply-email" required type="email" value={form.email} onChange={change("email")} className={inp} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Mobile">
              <input data-testid="apply-mobile" required value={form.mobile} onChange={change("mobile")} className={inp} />
            </Field>
            <Field label="Current location">
              <input data-testid="apply-location" required value={form.current_location} onChange={change("current_location")} className={inp} />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Total exp (yrs)">
              <input data-testid="apply-total-exp" required type="number" min={0} step={0.1} value={form.total_experience} onChange={change("total_experience")} className={inp} />
            </Field>
            <Field label="Relevant exp (yrs)">
              <input data-testid="apply-rel-exp" required type="number" min={0} step={0.1} value={form.relevant_experience} onChange={change("relevant_experience")} className={inp} />
            </Field>
            <Field label="Notice period">
              <input data-testid="apply-notice" required value={form.notice_period} onChange={change("notice_period")} placeholder="30 days" className={inp} />
            </Field>
          </div>
          <Field label="Current company">
            <input data-testid="apply-company" required value={form.current_company} onChange={change("current_company")} className={inp} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Current CTC (LPA)">
              <input data-testid="apply-ctc-current" required type="number" min={0} value={form.current_ctc} onChange={change("current_ctc")} className={inp} />
            </Field>
            <Field label="Expected CTC (LPA)">
              <input data-testid="apply-ctc-expected" required type="number" min={0} value={form.expected_ctc} onChange={change("expected_ctc")} className={inp} />
            </Field>
          </div>
          <Field label="Highest qualification">
            <input data-testid="apply-qualification" required value={form.highest_qualification} onChange={change("highest_qualification")} placeholder="B.Tech CSE - IIT Bombay" className={inp} />
          </Field>
          <Field label="Skills (comma separated)">
            <input data-testid="apply-skills" required value={form.skills} onChange={change("skills")} placeholder="React, TypeScript, Python" className={inp} />
          </Field>
          <Field label="Portfolio / LinkedIn URL">
            <input data-testid="apply-portfolio" required type="url" value={form.portfolio_url} onChange={change("portfolio_url")} placeholder="https://linkedin.com/in/yourname" className={inp} />
          </Field>

          <Field label="Resume (PDF, max 5 MB) — required">
            <input data-testid="apply-resume-file" type="file" accept="application/pdf" onChange={onFile} required={!form.resume_file_b64} className="block w-full text-sm text-quiet" />
            {form.resume_filename ? (
              <div className="text-xs font-mono text-[#10B981] mt-1.5">✓ Attached: {form.resume_filename}</div>
            ) : (
              <div className="text-xs font-mono text-quiet mt-1.5">No file attached yet — required to submit</div>
            )}
          </Field>
          <Field label="Cover note / additional context (optional)">
            <textarea data-testid="apply-resume-text" rows={4} value={form.resume_text} onChange={change("resume_text")} className={inp} placeholder="Anything additional you'd like to share with HR…" />
          </Field>

          <label className="flex items-start gap-3 pt-3 border-t border-rule">
            <input
              data-testid="apply-consent"
              type="checkbox"
              checked={form.consent}
              onChange={change("consent")}
              className="mt-1 accent-[#002FA7]"
            />
            <span className="text-sm text-quiet">
              I consent to the processing of my personal data for the purpose of recruitment and authorize the company
              to retain my profile in their talent pool.
            </span>
          </label>

          <button data-testid="apply-submit" type="submit" disabled={busy} className="w-full bg-klein hover:bg-klein-hover text-white px-5 py-3 text-sm font-medium disabled:opacity-50">
            {busy ? "Submitting…" : "Submit application →"}
          </button>
        </form>
      </div>
    </div>
  );
}

const inp = "w-full bg-transparent border border-rule px-3 py-2 focus:outline-none focus:border-klein focus:ring-1 focus:ring-klein font-sans text-sm";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);

const Tag = ({ children }) => (
  <span className="px-3 py-1 border border-ink text-xs font-mono uppercase tracking-wider text-ink bg-white">
    {children}
  </span>
);
